/**
 * O FECHAMENTO DO LOTE — o veredito do ciclo, calculado do livro-razão.
 *
 * Decidido pelo gestor em 06/10/2026, sobre a proposta em `docs/FECHAMENTO.md`:
 *
 *   1. CALCULADO NA HORA, não fotografado. A foto imutável é para o CUSTO
 *      (decisão 9), e entra quando o custo entrar. Peixe, peso e ração não
 *      mudam depois do encerramento.
 *   2. SOBREVIVÊNCIA E PESO MÉDIO SÓ DO TAMBAQUI — é o que entrou contado.
 *      DENSIDADE E CONVERSÃO COM TODAS AS ESPÉCIES — a ração alimentou a água
 *      inteira, e a densidade é o que a represa carregou. Vendas por espécie.
 *   3. TRANSFERÊNCIA QUE SAIU conta como sobrevivente, nunca como venda.
 *
 * Por que aqui e não na view `v_fechamento_ciclo` (001): ela conta a
 * transferência como venda, soma o piau ao tambaqui e mede faltas em dias.
 * E conta mora em `dominio/`, com teste — regra que vale para o projeto todo.
 *
 * O SALDO RESIDUAL É DIAGNÓSTICO, NUNCA ACUSAÇÃO (regra 4). A predação não
 * deixa nada para contar e só aparece aqui.
 */

import { romaneioGravado, type LinhaGravada } from './romaneio'
import { diasEntreDatas, m2ParaHa, m2 } from './unidades'

/** Uma linha válida de `mov_peixes` deste ciclo. */
export interface MovimentoDoLote {
  tipo: string
  data: string
  quantidade: number | null
  peso_total_kg: number | null
  peso_medio_g: number
  especie: string | null
  preco_kg_brl: number | null
  desconto_umidade: number | null
  /** Agrupa as linhas de uma mesma ficha de despesca. */
  registrado_em: string
  /** Do povoamento com remessa: quando o alevino chegou à fazenda. */
  remessa_chegada: string | null
}

export interface DadosDoLote {
  /** Entrada do peixe NESTE tanque (regra 2). */
  data_inicio: string
  /** Data do encerramento; nula se o ciclo ainda corre. */
  data_fim: string | null
  area_m2: number
  movimentos: MovimentoDoLote[]
  racao_kg: number
  dias_com_trato: number
  /** Dias com falta, por classificação. */
  faltas: { classificacao: string; dias: number }[]
  /** Dias distintos com alguma falta — a métrica da planilha antiga. */
  dias_com_falta: number
}

export interface Contagem {
  peixes: number
  kg: number
}

export interface VendaDoLote {
  /** A ficha da despesca: `registrado_em|tipo`. */
  chave: string
  /** As linhas como o banco as guardou — para reabrir o romaneio. */
  linhas: LinhaGravada[]
  descontoUmidade: number | null
  data: string
  encerrou: boolean
  peixes: number
  pesadoKg: number
  cobradoKg: number
  /** Nulo quando falta o preço de alguma espécie — total pela metade mente. */
  valorBrl: number | null
}

export interface Fechamento {
  dias: number | null
  /** Desde a chegada do alevino; nulo quando alguma entrada não é rastreável. */
  diasDesdeAlevino: number | null
  entraram: Contagem
  mortalidade: number
  perdas: number
  /** Tambaqui vendido (todas as despescas). */
  vendidos: Contagem
  transferidos: Contagem
  /** Outras espécies que saíram na rede — vendidas, fora da sobrevivência. */
  outrasEspecies: { especie: string; peixes: number; kg: number }[]
  sobrevivencia: number | null
  residual: number
  pesoEntradaG: number | null
  pesoSaidaG: number | null
  ganhoGDia: number | null
  /** Tudo que saiu da represa, todas as espécies, vendido ou transferido. */
  biomassaSaidaKg: number
  densidadeTHa: number | null
  racaoKg: number
  conversao: number | null
  kgHaDia: number | null
  diasComTrato: number
  faltas: { classificacao: string; dias: number }[]
  /** Dias com falta ÷ dias no tanque — só para comparar com a planilha. */
  pctDiasSemTratar: number | null
  vendas: VendaDoLote[]
  /** Soma das vendas; nula se alguma venda não tem todos os preços. */
  receitaBrl: number | null
  /** Receita ÷ quilos cobrados. */
  precoMedioKg: number | null
}

const ENTRADAS = new Set(['povoamento', 'transferencia_entrada'])
const DESPESCAS = new Set(['despesca_parcial', 'despesca_final'])

/**
 * A espécie do ciclo. Sem distinguir maiúscula: a lista de espécies é
 * parâmetro da fazenda (regra 10), e um «Tambaqui» digitado lá não pode jogar
 * a venda inteira para «outra espécie» e a sobrevivência para perto de zero.
 */
function ehTambaqui(especie: string | null): boolean {
  const e = (especie ?? '').trim().toLowerCase()
  return e === '' || e === 'tambaqui'
}

/** Peixes da linha: contados, ou deduzidos do peso quando entrou pesado. */
export function peixesDe(m: Pick<MovimentoDoLote, 'quantidade' | 'peso_total_kg' | 'peso_medio_g'>): number {
  if (m.quantidade !== null && m.quantidade > 0) return m.quantidade
  if (m.peso_total_kg !== null && m.peso_medio_g > 0) {
    return Math.round((m.peso_total_kg * 1000) / m.peso_medio_g)
  }
  return 0
}

function kgDe(m: Pick<MovimentoDoLote, 'quantidade' | 'peso_total_kg' | 'peso_medio_g'>): number {
  if (m.peso_total_kg !== null) return m.peso_total_kg
  return m.quantidade !== null ? (m.quantidade * m.peso_medio_g) / 1000 : 0
}

function somar(lista: MovimentoDoLote[]): Contagem {
  return lista.reduce((t, m) => ({ peixes: t.peixes + peixesDe(m), kg: t.kg + kgDe(m) }), { peixes: 0, kg: 0 })
}

export function fecharLote(d: DadosDoLote): Fechamento {
  const mov = d.movimentos
  const entradas = mov.filter((m) => ENTRADAS.has(m.tipo))
  const despescas = mov.filter((m) => DESPESCAS.has(m.tipo))
  const entraram = somar(entradas)
  const mortalidade = somar(mov.filter((m) => m.tipo === 'mortalidade')).peixes
  const perdas = somar(mov.filter((m) => m.tipo === 'perda_manejo')).peixes
  const vendidos = somar(despescas.filter((m) => ehTambaqui(m.especie)))
  const transferidos = somar(mov.filter((m) => m.tipo === 'transferencia_saida'))

  const outras = new Map<string, { peixes: number; kg: number }>()
  for (const m of despescas.filter((x) => !ehTambaqui(x.especie))) {
    const e = (m.especie as string).trim()
    const atual = outras.get(e) ?? { peixes: 0, kg: 0 }
    outras.set(e, { peixes: atual.peixes + peixesDe(m), kg: atual.kg + kgDe(m) })
  }
  const outrasEspecies = [...outras.entries()]
    .map(([especie, c]) => ({ especie, ...c }))
    .sort((a, b) => b.kg - a.kg)

  // Sobreviventes de tambaqui: os vendidos e os que foram para outra represa.
  const saiuPeixes = vendidos.peixes + transferidos.peixes
  const saiuKg = vendidos.kg + transferidos.kg
  const sobrevivencia = entraram.peixes > 0 ? saiuPeixes / entraram.peixes : null
  const residual = entraram.peixes - mortalidade - perdas - saiuPeixes

  const dias = d.data_fim ? diasEntreDatas(d.data_inicio, d.data_fim) : null
  const pesoEntradaG = entraram.peixes > 0 ? (entraram.kg * 1000) / entraram.peixes : null
  const pesoSaidaG = saiuPeixes > 0 ? (saiuKg * 1000) / saiuPeixes : null
  const ganhoGDia =
    pesoEntradaG !== null && pesoSaidaG !== null && dias !== null && dias > 0
      ? (pesoSaidaG - pesoEntradaG) / dias
      : null

  // A água inteira: todas as espécies, vendidas ou transferidas.
  const biomassaSaidaKg = despescas.reduce((t, m) => t + kgDe(m), 0) + transferidos.kg
  const ha = m2ParaHa(m2(d.area_m2))
  const densidadeTHa = ha > 0 ? biomassaSaidaKg / ha / 1000 : null
  const ganhoBiomassa = biomassaSaidaKg - entraram.kg
  // CA DESTA ENGORDA. Ganho que não houve (ou negativo) não tem conversão:
  // dividir por ele daria número sem sentido com cara de índice.
  const conversao = d.racao_kg > 0 && ganhoBiomassa > 0 ? d.racao_kg / ganhoBiomassa : null
  const kgHaDia = ha > 0 && dias !== null && dias > 0 ? d.racao_kg / ha / dias : null

  // TEMPO DESDE O ALEVINO — só no fechamento (regra 2), e só quando TODA
  // entrada veio de remessa com data. Uma transferência de outro ciclo, ou um
  // povoamento sem remessa, e a origem não é rastreável daqui: a tela diz
  // isso, em vez de inventar um número a partir de metade das entradas.
  const chegadas = entradas.map((m) => (m.tipo === 'povoamento' ? m.remessa_chegada : null))
  const diasDesdeAlevino =
    d.data_fim && chegadas.length > 0 && chegadas.every((c) => c !== null)
      ? diasEntreDatas(chegadas.reduce((a, b) => ((a as string) < (b as string) ? a : b)) as string, d.data_fim)
      : null

  // AS VENDAS, uma por ficha de despesca, pelo mesmo romaneio do papel.
  const fichas = new Map<string, MovimentoDoLote[]>()
  for (const m of despescas) {
    const k = `${m.registrado_em}|${m.tipo}`
    fichas.set(k, [...(fichas.get(k) ?? []), m])
  }
  const vendas: VendaDoLote[] = [...fichas.entries()]
    .map(([chave, linhas]) => {
      const primeira = linhas[0] as MovimentoDoLote
      const gravadas: LinhaGravada[] = linhas.map((m) => ({
        especie: m.especie,
        quantidade: peixesDe(m),
        peso_total_kg: kgDe(m),
        preco_kg_brl: m.preco_kg_brl,
      }))
      const r = romaneioGravado(gravadas, primeira.desconto_umidade)
      return {
        chave,
        linhas: gravadas,
        descontoUmidade: primeira.desconto_umidade,
        data: primeira.data,
        encerrou: primeira.tipo === 'despesca_final',
        peixes: r?.peixes ?? 0,
        pesadoKg: r?.pesadoKg ?? 0,
        cobradoKg: r?.cobradoKg ?? 0,
        valorBrl: r?.valorBrl ?? null,
      }
    })
    .sort((a, b) => a.data.localeCompare(b.data))
  const receitaBrl =
    vendas.length > 0 && vendas.every((v) => v.valorBrl !== null)
      ? Math.round(vendas.reduce((t, v) => t + (v.valorBrl as number), 0) * 100) / 100
      : null
  const cobrado = vendas.reduce((t, v) => t + v.cobradoKg, 0)
  const precoMedioKg = receitaBrl !== null && cobrado > 0 ? receitaBrl / cobrado : null

  return {
    dias,
    diasDesdeAlevino,
    entraram,
    mortalidade,
    perdas,
    vendidos,
    transferidos,
    outrasEspecies,
    sobrevivencia,
    residual,
    pesoEntradaG,
    pesoSaidaG,
    ganhoGDia,
    biomassaSaidaKg,
    densidadeTHa,
    racaoKg: d.racao_kg,
    conversao,
    kgHaDia,
    diasComTrato: d.dias_com_trato,
    faltas: d.faltas,
    pctDiasSemTratar: dias !== null && dias > 0 ? d.dias_com_falta / dias : null,
    vendas,
    receitaBrl,
    precoMedioKg,
  }
}
